import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../lib/navigation';
import { ArrowLeft, Users, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useThemeStore } from '../lib/themeStore';
import { usePageTracking } from '../lib/hooks/usePageTracking';
import { supabase } from '../lib/supabase';
import { TeamLogo } from '../components/TeamLogo';
import { EmbedBackButton } from '../components/shared/EmbedBackButton';
import { useEmbedStore } from '../lib/embedStore';
import { useTenantStore } from '../lib/tenantStore';
import './LineCombinationsEmbed.css';

const ARSENAL_FONTS_ID = 'dp-arsenal-embed-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineupRow {
  id: number;
  teamslug: string;
  teamabbrev: string;
  playerslug: string;
  playerid: number | null;
  line: string;
  position: string;
  lastupdated: string;
}

interface TeamEntry {
  teamslug: string;
  teamabbrev: string;
}

interface SkaterStats {
  kind: 'skater';
  gamesPlayed: number;
  goals: number;
  assists: number;
  points: number;
  salary: number | null;
}

interface GoalieStats {
  kind: 'goalie';
  gamesPlayed: number;
  wins: number;
  losses: number;
  otlosses: number;
  shutouts: number;
  savepct: number;
  salary: number | null;
}

type PlayerStats = SkaterStats | GoalieStats;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPlayerName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatLastUpdated(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function teamDisplayName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatSalary(salary: number | null): string {
  if (!salary) return '';
  if (salary >= 1_000_000) return `$${(salary / 1_000_000).toFixed(2)}M`;
  return `$${Math.round(salary / 1_000)}K`;
}

// Position configs: label + colour classes
const POSITION_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  lw:  { label: 'LG',   bg: '#f59e0b', text: '#1f2937' },
  c:   { label: 'C',    bg: '#3b82f6', text: '#ffffff' },
  rw:  { label: 'RD',   bg: '#10b981', text: '#ffffff' },
  ld:  { label: 'DG',   bg: '#06b6d4', text: '#ffffff' },
  rd:  { label: 'DD',   bg: '#8b5cf6', text: '#ffffff' },
  g1:  { label: 'G1',   bg: '#ef4444', text: '#ffffff' },
  g2:  { label: 'G2',   bg: '#6b7280', text: '#ffffff' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Tries to show NHL headshot; falls back to TeamLogo on error */
function PlayerPhoto({
  playerId,
  teamAbbrev,
  size,
}: {
  playerId: number;
  teamAbbrev: string;
  size: number;
}) {
  const [imgError, setImgError] = useState(false);
  const url = `https://assets.nhle.com/mugs/nhl/20242025/${teamAbbrev}/${playerId}.png`;

  if (imgError) {
    return <TeamLogo abbrev={teamAbbrev} size={size} />;
  }
  return (
    <img
      src={url}
      alt=""
      className="lc-photo"
      width={size}
      height={size}
      style={{ borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(59,130,246,0.25)' }}
      onError={() => setImgError(true)}
    />
  );
}

interface PlayerSlotProps {
  player: LineupRow | null;
  teamAbbrev?: string;
  playerId?: number;
  theme: string;
  playerStats?: PlayerStats;
}

function PlayerSlot({ player, teamAbbrev, playerId, theme, playerStats }: PlayerSlotProps) {
  const isDark = theme === 'dark';
  const pos = player?.position ?? '';
  const cfg = POSITION_CONFIG[pos] ?? { label: pos.toUpperCase(), bg: '#6b7280', text: '#fff' };

  return (
    <div
      className="lc-slot"
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '10px 6px',
        borderRadius: '10px',
        background: isDark
          ? 'rgba(30, 41, 59, 0.7)'
          : 'rgba(255, 255, 255, 0.85)',
        border: isDark
          ? '1px solid rgba(59,130,246,0.15)'
          : '1px solid rgba(203,213,225,0.6)',
        gap: 6,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        cursor: 'default',
        boxShadow: isDark
          ? '0 2px 8px rgba(0,0,0,0.3)'
          : '0 2px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* Position badge */}
      <span
        className="lc-pos"
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '20px',
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.05em',
          backgroundColor: cfg.bg,
          color: cfg.text,
          lineHeight: 1.5,
        }}
      >
        {cfg.label}
      </span>

      {/* Player headshot (or team logo fallback) */}
      {player && playerId && teamAbbrev ? (
        <PlayerPhoto playerId={playerId} teamAbbrev={teamAbbrev} size={56} />
      ) : teamAbbrev ? (
        <TeamLogo abbrev={teamAbbrev} size={44} />
      ) : null}

      {/* Player name */}
      <span
        className="lc-slot-name"
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textAlign: 'center',
          color: isDark ? '#e2e8f0' : '#1e293b',
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
      >
        {player ? formatPlayerName(player.playerslug) : '—'}
      </span>

      {/* Season stats — only shown when playerid is known and today's data exists */}
      {playerId && playerStats?.kind === 'skater' && (
        <div className="lc-chips" style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.58rem', background: 'rgba(148,163,184,0.12)', color: isDark ? '#94a3b8' : '#64748b', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>PJ {playerStats.gamesPlayed}</span>
          <span style={{ fontSize: '0.58rem', background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>B {playerStats.goals}</span>
          <span style={{ fontSize: '0.58rem', background: 'rgba(59,130,246,0.12)', color: '#3b82f6', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>A {playerStats.assists}</span>
          <span style={{ fontSize: '0.58rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>PTS {playerStats.points}</span>
        </div>
      )}
      {playerId && playerStats?.salary ? (
        <span className="lc-salary" style={{ fontSize: '0.6rem', fontWeight: 700, color: isDark ? '#fbbf24' : '#d97706' }}>
          {formatSalary(playerStats.salary)}
        </span>
      ) : null}
    </div>
  );
}

interface LineCardProps {
  lineLabel: string;
  lineNumber: string;
  players: LineupRow[];
  positionOrder: string[];
  teamAbbrev: string;
  theme: string;
  accentColor: string;
  playerIdMap: Map<string, number>;
  playerStatsMap: Map<string, PlayerStats>;
}

function LineCard({ lineLabel, lineNumber, players, positionOrder, teamAbbrev, theme, accentColor, playerIdMap, playerStatsMap }: LineCardProps) {
  const isDark = theme === 'dark';

  // Map by position
  const byPos: Record<string, LineupRow> = {};
  players.forEach((p) => { byPos[p.position] = p; });

  return (
    <div
      className="lc-line"
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(135deg, rgba(15,23,42,0.6) 0%, rgba(30,41,59,0.6) 100%)'
          : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.9) 100%)',
        border: isDark ? '1px solid rgba(59,130,246,0.12)' : '1px solid rgba(203,213,225,0.5)',
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.06)',
      }}
    >
      {/* Line header */}
      <div
        className="lc-line-header"
        style={{
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: `linear-gradient(90deg, ${accentColor}22, transparent)`,
          borderBottom: isDark ? '1px solid rgba(59,130,246,0.1)' : '1px solid rgba(203,213,225,0.4)',
        }}
      >
        <span
          className="lc-line-num"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: accentColor,
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.8rem',
          }}
        >
          {lineNumber}
        </span>
        <span
          className="lc-line-label"
          style={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: isDark ? '#cbd5e1' : '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {lineLabel}
        </span>
      </div>

      {/* Players */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '12px',
        }}
      >
        {positionOrder.map((pos) => {
          const rowPlayer = byPos[pos] ?? null;
          const pid = rowPlayer ? playerIdMap.get(rowPlayer.playerslug) : undefined;
          return (
            <PlayerSlot
              key={pos}
              player={rowPlayer}
              teamAbbrev={teamAbbrev}
              playerId={pid}
              theme={theme}
              playerStats={rowPlayer ? playerStatsMap.get(rowPlayer.playerslug) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  theme: string;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, icon, children, theme, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isDark = theme === 'dark';

  return (
    <div
      className="lc-section"
      style={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: isDark ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(203,213,225,0.5)',
        marginBottom: 16,
      }}
    >
      <button
        className="lc-section-btn"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open
            ? (isDark ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(203,213,225,0.4)')
            : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon}
          <span
            className="lc-section-title"
            style={{
              fontWeight: 700,
              fontSize: '1rem',
              color: isDark ? '#f1f5f9' : '#1e293b',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {title}
          </span>
        </div>
        {open
          ? <ChevronUp size={18} color={isDark ? '#94a3b8' : '#64748b'} />
          : <ChevronDown size={18} color={isDark ? '#94a3b8' : '#64748b'} />
        }
      </button>

      {open && (
        <div
          className="lc-section-body"
          style={{
            padding: '16px',
            background: isDark
              ? 'rgba(15,23,42,0.4)'
              : 'rgba(248,250,252,0.6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Goalie row ───────────────────────────────────────────────────────────────

function GoalieRow({
  players,
  teamAbbrev,
  theme,
  playerIdMap,
  playerStatsMap,
}: {
  players: LineupRow[];
  teamAbbrev: string;
  theme: string;
  playerIdMap: Map<string, number>;
  playerStatsMap: Map<string, PlayerStats>;
}) {
  const isDark = theme === 'dark';
  const starter = players.find((p) => p.position === 'g1');
  const backup  = players.find((p) => p.position === 'g2');

  const GoalieCard = ({ player, label }: { player: LineupRow | null; label: string }) => {
    const pid = player ? playerIdMap.get(player.playerslug) : undefined;
    const rawStats = (player && pid) ? playerStatsMap.get(player.playerslug) : undefined;
    const gStats = rawStats?.kind === 'goalie' ? rawStats : undefined;
    return (
    <div
      className="lc-slot lc-goalie"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '16px 12px',
        borderRadius: '12px',
        background: isDark ? 'rgba(30,41,59,0.7)' : 'rgba(255,255,255,0.85)',
        border: isDark ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(203,213,225,0.6)',
        gap: 8,
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.06)',
      }}
    >
      <span
        className="lc-goalie-label"
        style={{
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: isDark ? '#94a3b8' : '#64748b',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {player && pid ? (
        <PlayerPhoto playerId={pid} teamAbbrev={teamAbbrev} size={72} />
      ) : (
        <TeamLogo abbrev={teamAbbrev} size={56} />
      )}
      <span
        className="lc-slot-name"
        style={{
          fontWeight: 700,
          fontSize: '0.9rem',
          color: isDark ? '#e2e8f0' : '#1e293b',
          textAlign: 'center',
        }}
      >
        {player ? formatPlayerName(player.playerslug) : '—'}
      </span>

      {/* Season stats — wins / losses / shutouts */}
      {gStats && (
        <div className="lc-chips" style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.6rem', background: 'rgba(148,163,184,0.12)', color: isDark ? '#94a3b8' : '#64748b', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>PJ {gStats.gamesPlayed}</span>
          <span style={{ fontSize: '0.6rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>V {gStats.wins}</span>
          <span style={{ fontSize: '0.6rem', background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>D {gStats.losses}</span>
          <span style={{ fontSize: '0.6rem', background: 'rgba(99,102,241,0.12)', color: '#6366f1', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>SO {gStats.shutouts}</span>
        </div>
      )}
      {gStats?.salary ? (
        <span className="lc-salary" style={{ fontSize: '0.6rem', fontWeight: 700, color: isDark ? '#fbbf24' : '#d97706' }}>
          {formatSalary(gStats.salary)}
        </span>
      ) : null}

      {player && (
        <span
          className="lc-role"
          style={{
            padding: '3px 10px',
            borderRadius: '20px',
            fontSize: '0.65rem',
            fontWeight: 700,
            backgroundColor: player.position === 'g1' ? '#ef4444' : '#6b7280',
            color: '#fff',
          }}
        >
          {player.position === 'g1' ? 'Partant' : 'Substitut'}
        </span>
      )}
    </div>
    );
  };

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <GoalieCard player={starter ?? null} label="Partant" />
      <GoalieCard player={backup ?? null}  label="Substitut" />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LineCombinations() {
  usePageTracking('linecombination');

  const navigate = useNavigate();
  const { theme: storeTheme } = useThemeStore();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const tenantId = useTenantStore((state) => state.tenantId);
  const isTslhEmbed = isEmbed && tenantId === 'tslh';
  useArsenalFonts(isEmbed);
  // En embed, le thème suit le tenant (Arsenal = clair, TSLH = sombre), pas le thème de l'utilisateur.
  const theme = isEmbed ? (isTslhEmbed ? 'dark' : 'light') : storeTheme;
  const isDark = theme === 'dark';

  const [teams, setTeams] = useState<TeamEntry[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamEntry | null>(null);
  const [lineup, setLineup] = useState<LineupRow[]>([]);
  const [playerIdMap, setPlayerIdMap] = useState<Map<string, number>>(new Map());
  const [playerStatsMap, setPlayerStatsMap] = useState<Map<string, PlayerStats>>(new Map());
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingLineup, setLoadingLineup] = useState(false);

  useEffect(() => {
    async function loadTeams() {
      setLoadingTeams(true);
      const { data, error } = await supabase
        .from('lineupcombination')
        .select('teamslug, teamabbrev')
        .order('teamslug');

      if (error) {
        console.error('Error loading teams:', error);
        setLoadingTeams(false);
        return;
      }

      // Deduplicate
      const map = new Map<string, TeamEntry>();
      (data ?? []).forEach((row: TeamEntry) => {
        if (!map.has(row.teamslug)) map.set(row.teamslug, row);
      });

      const sorted = Array.from(map.values()).sort((a, b) =>
        a.teamslug.localeCompare(b.teamslug)
      );
      setTeams(sorted);
      setLoadingTeams(false);

      // Auto-sélectionner les Ducks par défaut
      const ducks = sorted.find((t) => t.teamslug === 'anaheim-ducks') ?? sorted[0] ?? null;
      if (ducks) {
        setSelectedTeam(ducks);
        setLoadingLineup(true);
      }
    }
    loadTeams();
  }, []);

  // Load lineup whenever the selected team changes.
  useEffect(() => {
    if (!selectedTeam) return;

    let cancelled = false;
    setLoadingLineup(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('lineupcombination')
          .select('*')
          .eq('teamslug', selectedTeam.teamslug)
          .order('line')
          .order('position');

        if (cancelled) return;
        if (error) { console.error('Error loading lineup:', error); return; }

        const rows = data ?? [];

        // Build playerIdMap directly from the playerid column — no extra lookups needed
        const newMap = new Map<string, number>();
        rows.forEach((r: LineupRow) => {
          if (r.playerid) newMap.set(r.playerslug, r.playerid);
        });

        // ── Fetch season stats ─────────────────────────────────────────────
        const newStatsMap = new Map<string, PlayerStats>();
        if (newMap.size > 0) {
          const idToSlug = new Map<number, string>();
          newMap.forEach((id, slug) => idToSlug.set(id, slug));

          const skaterIds = rows
            .filter((r: LineupRow) => r.line !== 'g' && r.playerid)
            .map((r: LineupRow) => r.playerid as number);
          const goalieIds = rows
            .filter((r: LineupRow) => r.line === 'g' && r.playerid)
            .map((r: LineupRow) => r.playerid as number);

          const [skatersStatsResp, goaliesStatsResp] = await Promise.all([
            skaterIds.length > 0
              ? supabase.from('show_player_onlycurrentday')
                  .select('playerid, goals, assists, points, salary, gamesplayed')
                  .in('playerid', skaterIds)
              : Promise.resolve({ data: [] as any[] }),
            goalieIds.length > 0
              ? supabase.from('show_goalie_onlycurrentday')
                  .select('playerid, wins, losses, otlosses, shutouts, savepct, salary, gamesplayed')
                  .in('playerid', goalieIds)
              : Promise.resolve({ data: [] as any[] }),
          ]);

          if (cancelled) return;

          (skatersStatsResp.data ?? []).forEach((p: any) => {
            const slug = idToSlug.get(p.playerid);
            if (slug) newStatsMap.set(slug, {
              kind: 'skater',
              gamesPlayed: p.gamesplayed ?? 0,
              goals:    p.goals    ?? 0,
              assists:  p.assists  ?? 0,
              points:   p.points   ?? 0,
              salary:   p.salary   ?? null,
            });
          });
          (goaliesStatsResp.data ?? []).forEach((g: any) => {
            const slug = idToSlug.get(g.playerid);
            if (slug) newStatsMap.set(slug, {
              kind: 'goalie',
              gamesPlayed: g.gamesplayed ?? 0,
              wins:     g.wins     ?? 0,
              losses:   g.losses   ?? 0,
              otlosses: g.otlosses ?? 0,
              shutouts: g.shutouts ?? 0,
              savepct:  g.savepct  ?? 0,
              salary:   g.salary   ?? null,
            });
          });
        }

        if (cancelled) return;

        setPlayerIdMap(newMap);
        setPlayerStatsMap(newStatsMap);
        setLineup(rows);
      } catch (err) {
        console.error('Error loading lineup:', err);
      } finally {
        if (!cancelled) setLoadingLineup(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedTeam]);

  // Derived data
  const lastUpdated = lineup[0]?.lastupdated ?? null;

  // Forward lines: f1…f4
  const forwardLines = ['f1', 'f2', 'f3', 'f4'].map((line) => ({
    line,
    label: `Trio ${line.charAt(1)}`,
    number: line.charAt(1),
    players: lineup.filter((p) => p.line === line),
    positions: ['lw', 'c', 'rw'],
    accent: '#3b82f6',
  })).filter((l) => l.players.length > 0);

  // Defense pairs: d1…d3
  const defenseLines = ['d1', 'd2', 'd3'].map((line) => ({
    line,
    label: `Paire ${line.charAt(1)}`,
    number: line.charAt(1),
    players: lineup.filter((p) => p.line === line),
    positions: ['ld', 'rd'],
    accent: '#8b5cf6',
  })).filter((l) => l.players.length > 0);

  // Goalies
  const goalies = lineup.filter((p) => p.line === 'g');

  const cardBg = isDark
    ? 'linear-gradient(135deg, rgba(30,41,59,0.8) 0%, rgba(15,23,42,0.8) 100%)'
    : 'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(241,245,249,0.95) 100%)';

  return (
    <div className="container-fluid px-3 px-md-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div
            className={`card shadow-lg border-0 lc-card${isEmbed ? ' line-combinations-embed' : ''}`}
            style={{
              background: cardBg,
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
            }}
          >
            <div className="card-body p-3 p-md-4">

              {/* ── Header ── */}
              <EmbedBackButton />
              <div className="lc-header d-flex align-items-center justify-content-between mb-4">
                {!isEmbed && (
                <button
                  className="btn btn-link p-0 d-flex align-items-center"
                  style={{ color: isDark ? '#ffffff' : '#0d6efd', textDecoration: 'none' }}
                  onClick={() => goBackOrHome(navigate)}
                >
                  <ArrowLeft size={20} className="me-2" />
                  <span className="d-none d-sm-inline">Retour</span>
                </button>
                )}

                <div className="lc-header-title text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                    {!isEmbed && <Users size={28} style={{ color: '#3b82f6' }} />}
                    <h1
                      className="mb-0 fw-bold"
                      style={{ fontSize: 'clamp(1.1rem, 3vw, 1.6rem)', color: isDark ? '#f1f5f9' : '#1e293b' }}
                    >
                      Combinaisons de Lignes
                    </h1>
                  </div>
                  <p className="text-muted mb-0 small">Alignements des équipes NHL</p>
                </div>

                {!isEmbed && <div style={{ width: 80 }} />}
              </div>

              {/* ── Team selector ── */}
              {loadingTeams ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              ) : (
                <div
                  className="lc-selector mb-4 p-3"
                  style={{
                    borderRadius: '14px',
                    background: isDark
                      ? 'rgba(15,23,42,0.5)'
                      : 'rgba(241,245,249,0.7)',
                    border: isDark
                      ? '1px solid rgba(59,130,246,0.15)'
                      : '1px solid rgba(203,213,225,0.5)',
                  }}
                >
                  <p
                    className="lc-selector-label fw-semibold mb-3 small text-uppercase"
                    style={{
                      letterSpacing: '0.08em',
                      color: isDark ? '#94a3b8' : '#64748b',
                    }}
                  >
                    Sélectionner une équipe
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    {teams.map((team) => {
                      const isSelected = selectedTeam?.teamslug === team.teamslug;
                      return (
                        <button
                          key={team.teamslug}
                          className={`lc-team-btn${isSelected ? ' is-selected' : ''}`}
                          aria-pressed={isSelected}
                          onClick={() => { setSelectedTeam(team); setLoadingLineup(true); }}
                          title={teamDisplayName(team.teamslug)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 52,
                            height: 52,
                            borderRadius: '12px',
                            border: isSelected
                              ? '2px solid #3b82f6'
                              : isDark
                                ? '1px solid rgba(59,130,246,0.12)'
                                : '1px solid rgba(203,213,225,0.45)',
                            background: isSelected
                              ? (isDark ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.07)')
                              : isDark
                                ? 'rgba(30,41,59,0.5)'
                                : 'rgba(255,255,255,0.7)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: isSelected
                              ? '0 0 0 3px rgba(59,130,246,0.22)'
                              : isDark
                                ? '0 1px 4px rgba(0,0,0,0.25)'
                                : '0 1px 3px rgba(0,0,0,0.07)',
                            padding: 0,
                          }}
                        >
                          <TeamLogo abbrev={team.teamabbrev} size={36} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Lineup content ── */}
              {selectedTeam && (
                <>
                  {/* Team title + last updated */}
                  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <TeamLogo abbrev={selectedTeam.teamabbrev} size={48} />
                      <div>
                        <h2
                          className="lc-team-title mb-0 fw-bold"
                          style={{ fontSize: 'clamp(1rem, 2.5vw, 1.4rem)', color: isDark ? '#f1f5f9' : '#1e293b' }}
                        >
                          {teamDisplayName(selectedTeam.teamslug)}
                        </h2>
                        <span
                          className="lc-team-abbrev"
                          style={{
                            fontSize: '0.75rem',
                            color: isDark ? '#94a3b8' : '#64748b',
                          }}
                        >
                          {selectedTeam.teamabbrev}
                        </span>
                      </div>
                    </div>
                    {lastUpdated && (
                      <div
                        className="lc-updated"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 12px',
                          borderRadius: '20px',
                          background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.08)',
                          border: '1px solid rgba(59,130,246,0.2)',
                        }}
                      >
                        <Clock size={13} style={{ color: '#3b82f6' }} />
                        <span style={{ fontSize: '0.72rem', color: isDark ? '#93c5fd' : '#2563eb', fontWeight: 600 }}>
                          Mis à jour : {formatLastUpdated(lastUpdated)}
                        </span>
                      </div>
                    )}
                  </div>

                  {loadingLineup ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* ── ATTAQUANTS ── */}
                      {forwardLines.length > 0 && (
                        <CollapsibleSection
                          title={isEmbed ? 'Attaquants' : '⚡ Attaquants'}
                          icon={
                            <span className="lc-section-icon" style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                            }}>🏒</span>
                          }
                          theme={theme}
                          defaultOpen={true}
                        >
                          {forwardLines.map((fl) => (
                            <LineCard
                              key={fl.line}
                              lineLabel={fl.label}
                              lineNumber={fl.number}
                              players={fl.players}
                              positionOrder={fl.positions}
                              teamAbbrev={selectedTeam.teamabbrev}
                              theme={theme}
                              accentColor={fl.accent}
                              playerIdMap={playerIdMap}
                              playerStatsMap={playerStatsMap}
                            />
                          ))}
                        </CollapsibleSection>
                      )}

                      {/* ── DÉFENSEURS ── */}
                      {defenseLines.length > 0 && (
                        <CollapsibleSection
                          title={isEmbed ? 'Défenseurs' : '🛡 Défenseurs'}
                          icon={
                            <span className="lc-section-icon" style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                            }}>🛡️</span>
                          }
                          theme={theme}
                          defaultOpen={true}
                        >
                          {defenseLines.map((dl) => (
                            <LineCard
                              key={dl.line}
                              lineLabel={dl.label}
                              lineNumber={dl.number}
                              players={dl.players}
                              positionOrder={dl.positions}
                              teamAbbrev={selectedTeam.teamabbrev}
                              theme={theme}
                              accentColor={dl.accent}
                              playerIdMap={playerIdMap}
                              playerStatsMap={playerStatsMap}
                            />
                          ))}
                        </CollapsibleSection>
                      )}

                      {/* ── GARDIENS ── */}
                      {goalies.length > 0 && (
                        <CollapsibleSection
                          title={isEmbed ? 'Gardiens' : '🥅 Gardiens'}
                          icon={
                            <span className="lc-section-icon" style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                            }}>🥅</span>
                          }
                          theme={theme}
                          defaultOpen={true}
                        >
                          <GoalieRow
                            players={goalies}
                            teamAbbrev={selectedTeam.teamabbrev}
                            theme={theme}
                            playerIdMap={playerIdMap}
                            playerStatsMap={playerStatsMap}
                          />
                        </CollapsibleSection>
                      )}

                      {forwardLines.length === 0 && defenseLines.length === 0 && goalies.length === 0 && (
                        <div
                          className="text-center py-5"
                          style={{ color: isDark ? '#94a3b8' : '#64748b' }}
                        >
                          <Users size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                          <p className="mb-0">Aucune donnée disponible pour cette équipe.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
